import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "units/dashboard/context/LanguageProvider";

const FLAGS = { fa: "🇮🇷", ar: "🇸🇦", en: "🇬🇧" };

export default function LanguageToggle() {
  const { lang, setLang, languages } = useLanguage();
  const { t } = useTranslation("landing");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const current = languages.find((l) => l.code === lang) || languages[0];

  /* ---------- بستن با کلیک بیرون ---------- */
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSelect = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("a11y.changeLanguage")}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={clsx(
          "flex h-[34px] items-center gap-1.5 rounded-full border-[1.5px] border-line bg-surface-2 px-2.5 text-[12px] font-bold text-ink",
          "transition-all duration-200 hover:border-teal-2",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-2 focus-visible:ring-offset-2",
          "sm:gap-2 sm:px-3.5 sm:text-[13px]"
        )}
      >
        <Globe className="h-4 w-4 text-teal-2" />
        <span className="hidden sm:inline">{current.label}</span>
        <ChevronDown
          className={clsx(
            "h-3 w-3 text-ink-mute transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className={clsx(
              "absolute top-full z-50 mt-2 w-40 rounded-2xl border border-line bg-surface p-1.5 shadow-card",
              // در RTL از راست تراز، در LTR از چپ
              "end-0"
            )}
          >
            {languages.map((l) => {
              const active = l.code === lang;
              return (
                <li key={l.code} role="option" aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => handleSelect(l.code)}
                    className={clsx(
                      "flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold transition-colors",
                      active
                        ? "bg-teal-soft text-teal dark:!bg-teal-200 dark:!text-slate-3"
                        : "text-ink hover:bg-surface-2"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span aria-hidden="true">{FLAGS[l.code]}</span>
                      <span>{l.label}</span>
                    </span>
                    {active && <Check className="h-4 w-4" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}